import React, { useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Tabs,
  Tab,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  History as HistoryIcon,
  Add as AddIcon,
  Upload as ImportIcon
} from '@mui/icons-material';
import { useBrand } from '../../../context/BrandContext';

// Tab components
import PhysicalCountHistory from './PhysicalCountHistory';
import PhysicalCountImport from './PhysicalCountImport';

// Import the existing wizard component (renamed import)
import PhysicalCountWizard from '../PhysicalCounts';

/**
 * PhysicalCountsContainer - Main container with tabbed interface
 *
 * Tab 1: Count History - View past counts, expand details, delete
 * Tab 2: New Count - The existing wizard for manual counting
 * Tab 3: Import CSV - Upload and import physical count data
 */
function PhysicalCountsContainer() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedBrand, brands, selectBrand } = useBrand();

  // Read initial tab from URL or default to history (0)
  const [activeTab, setActiveTab] = useState(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'new') return 1;
    if (tabParam === 'import') return 2;
    return 0; // history is default
  });

  const handleTabChange = useCallback((event, newValue) => {
    setActiveTab(newValue);

    // Update URL param
    const newParams = new URLSearchParams(searchParams);
    if (newValue === 0) {
      newParams.delete('tab');
    } else if (newValue === 1) {
      newParams.set('tab', 'new');
    } else if (newValue === 2) {
      newParams.set('tab', 'import');
    }
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleBrandChange = (event) => {
    const brandId = event.target.value;
    const brand = brands.find(b => b.id === brandId);
    if (brand) {
      selectBrand(brand);
    }
  };

  const handleImportComplete = useCallback((result) => {
    // Switch to history tab to see the new counts
    setActiveTab(0);
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('tab');
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Physical Counts
        </Typography>

        {/* Brand selector */}
        <FormControl sx={{ minWidth: 200 }} size="small">
          <InputLabel>Brand</InputLabel>
          <Select
            value={selectedBrand?.id || ''}
            onChange={handleBrandChange}
            label="Brand"
          >
            {brands.map((brand) => (
              <MenuItem key={brand.id} value={brand.id}>
                {brand.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Tabs */}
      <Card sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            icon={<HistoryIcon />}
            iconPosition="start"
            label="Count History"
          />
          <Tab
            icon={<AddIcon />}
            iconPosition="start"
            label="New Count"
          />
          <Tab
            icon={<ImportIcon />}
            iconPosition="start"
            label="Import CSV"
          />
        </Tabs>

        <CardContent sx={{ p: 0 }}>
          {/* Tab 0: Count History */}
          {activeTab === 0 && (
            <Box sx={{ p: 3 }}>
              <PhysicalCountHistory
                companyId={selectedBrand?.company_id}
                brandId={selectedBrand?.id}
              />
            </Box>
          )}

          {/* Tab 1: New Count (existing wizard) */}
          {activeTab === 1 && (
            <Box sx={{ p: 0 }}>
              <PhysicalCountWizard embedded />
            </Box>
          )}

          {/* Tab 2: Import CSV */}
          {activeTab === 2 && (
            <Box sx={{ p: 3 }}>
              <PhysicalCountImport
                companyId={selectedBrand?.company_id}
                onImportComplete={handleImportComplete}
              />
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

export default PhysicalCountsContainer;
